import React, { useState, useEffect } from 'react';
import { testimonials } from '../data/mock';
import { Star, ChevronLeft, ChevronRight, Quote } from 'lucide-react';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Number of testimonials to show at once (responsive)
  const getVisibleCount = () => {
    if (typeof window !== 'undefined') {
      if (window.innerWidth < 768) return 1;
      if (window.innerWidth < 1024) return 2;
      return 3;
    }
    return 3;
  };

  const [visibleCount, setVisibleCount] = useState(3);

  useEffect(() => {
    const handleResize = () => setVisibleCount(getVisibleCount());
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-rotate testimonials
  useEffect(() => {
    if (!isAutoPlaying) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => 
        prev + 1 >= testimonials.length ? 0 : prev + 1
      );
    }, 5000);
    return () => clearInterval(interval);
  }, [isAutoPlaying]);

  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => 
      prev + 1 >= testimonials.length ? 0 : prev + 1
    );
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => 
      prev - 1 < 0 ? testimonials.length - 1 : prev - 1
    );
  };

  const getVisibleTestimonials = () => {
    const visible = [];
    for (let i = 0; i < visibleCount; i++) {
      const index = (currentIndex + i) % testimonials.length;
      visible.push(testimonials[index]);
    }
    return visible;
  };

  return (
    <section id="testimonials" className="section-padding bg-[#f8fafc]">
      <div className="container">
        {/* Header */}
        <div className="max-w-3xl mx-auto text-center mb-12">
          <p className="text-sm uppercase tracking-widest text-[#b8860b] font-medium mb-4">
            Patient Testimonials
          </p>
          <h2 className="heading-1 text-[#0f172a] mb-4">
            Trusted by Patients Worldwide
          </h2>
          <p className="body-regular text-[#475569]">
            Read what patients say about their experience with Dr. Castellon
          </p>
          <div className="flex items-center justify-center gap-2 mt-4">
            <div className="flex">
              {[...Array(5)].map((_, i) => (
                <Star key={i} size={20} className="fill-[#b8860b] text-[#b8860b]" />
              ))}
            </div>
            <span className="text-sm text-[#475569]">97 reviews on Doctoralia</span>
          </div>
        </div>

        {/* Testimonials Carousel */}
        <div className="relative">
          {/* Navigation Buttons */}
          <button
            onClick={prevSlide}
            className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 lg:-translate-x-12 z-10 w-10 h-10 bg-white border border-[#e2e8f0] flex items-center justify-center hover:bg-[#0f172a] hover:text-white transition-colors"
            aria-label="Previous testimonial"
          >
            <ChevronLeft size={20} />
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 lg:translate-x-12 z-10 w-10 h-10 bg-white border border-[#e2e8f0] flex items-center justify-center hover:bg-[#0f172a] hover:text-white transition-colors"
            aria-label="Next testimonial"
          >
            <ChevronRight size={20} />
          </button>

          {/* Testimonial Cards */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 px-4">
            {getVisibleTestimonials().map((testimonial, index) => (
              <div
                key={`${testimonial.id}-${index}`}
                className="bg-white p-8 border border-[#e2e8f0] flex flex-col transition-all duration-300"
              >
                {/* Quote Icon */}
                <div className="mb-4">
                  <Quote size={32} className="text-[#b8860b] opacity-50" />
                </div>

                {/* Stars */}
                <div className="flex mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="fill-[#b8860b] text-[#b8860b]" />
                  ))}
                </div>

                {/* Text */}
                <p className="text-[#475569] text-sm leading-relaxed flex-grow mb-6">
                  "{testimonial.text}"
                </p>

                {/* Author */}
                <div className="border-t border-[#e2e8f0] pt-4 mt-auto">
                  <p className="font-semibold text-[#0f172a]">{testimonial.name}</p>
                  <p className="text-xs text-[#64748b]">{testimonial.type}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Dots Indicator */}
        <div className="flex justify-center gap-2 mt-8">
          {testimonials.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setIsAutoPlaying(false);
                setCurrentIndex(index);
              }}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'bg-[#0f172a] w-6' : 'bg-[#cbd5e1]'
              }`}
              aria-label={`Go to testimonial ${index + 1}`}
            />
          ))}
        </div>

        {/* View All Link */}
        <div className="text-center mt-8">
          <a
            href="https://www.doctoralia.es/carlos-castellon-m-d/medico-general/ibiza#profile-reviews"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-[#0f172a] font-medium hover:text-[#b8860b] transition-colors"
          >
            View all 97 reviews on Doctoralia →
          </a>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
